import { useState } from 'react';
import {
    Calendar,
    Clock,
    Users,
    TrendingUp,
    AlertCircle,
    ChevronRight,
    Target,
    DollarSign,
    BarChart,
    CheckCircle
} from 'lucide-react';
const Main = () => {
    const [selectedWeek, setSelectedWeek] = useState('current');

    // Mock data for targets and progress
    const targetMetrics = {
        revenue: {
            target: 500000,
            current: 325000,
            projected: 515000
        },
        meetings: {
            target: 45,
            completed: 28,
            scheduled: 12
        },
        conversion: {
            rate: "62%",
            trend: "up"
        }
    };

    // Mock prioritized leads
    const prioritizedLeads = [
        {
            name: "TechVision AI",
            opportunity: "£75,000",
            probability: 85,
            urgency: "high",
            bestTimes: ["Tuesday 10:00", "Wednesday 14:00"],
            type: "Portfolio Strategy",
            decisionMaker: "Sarah Chen (CTO)"
        },
        {
            name: "BioInnovate Ltd",
            opportunity: "£120,000",
            probability: 75,
            urgency: "medium",
            bestTimes: ["Monday 11:00", "Thursday 15:00"],
            type: "IP Protection",
            decisionMaker: "Dr. James Wilson"
        },
        {
            name: "FinSecure Global",
            opportunity: "£95,000",
            probability: 70,
            urgency: "high",
            bestTimes: ["Wednesday 09:00", "Friday 11:00"],
            type: "Trademark Portfolio",
            decisionMaker: "Michael Zhang (CEO)"
        }
    ];

    // Mock suggested schedule
    const suggestedSchedule = [
        {
            day: "Monday",
            slots: [
                {
                    time: "09:30 - 10:30",
                    lead: "Internal Review",
                    type: "Planning",
                    status: "confirmed"
                },
                {
                    time: "11:00 - 12:00",
                    lead: "BioInnovate Ltd",
                    type: "First Meeting",
                    status: "suggested",
                    value: "£120,000"
                }
            ]
        },
        {
            day: "Tuesday",
            slots: [
                {
                    time: "10:00 - 11:00",
                    lead: "TechVision AI",
                    type: "Portfolio Review",
                    status: "suggested",
                    value: "£75,000"
                }
            ]
        }
    ];
    return (
        <div className="space-y-6">
            {/* Metrics Overview */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        <span className='font-bold'>Progress to Targets</span>
                    </div>
                </div>
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                        <div className="p-4 border rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <div className="text-sm text-gray-500">Revenue</div>
                                    <div className="text-2xl font-bold">£{(targetMetrics.revenue.current / 1000).toFixed(1)}k</div>
                                </div>
                                <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                    Target: £{(targetMetrics.revenue.target / 1000).toFixed(1)}k
                                </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div className="bg-green h-2 rounded-full" style={{ width: `65%` }}></div>
                            </div>
                            <div className="mt-2 text-sm text-[#16a34a]">
                                On track (+£{((targetMetrics.revenue.projected - targetMetrics.revenue.target) / 1000).toFixed(1)}k)
                            </div>
                        </div>

                        <div className="p-4 border rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <div className="text-sm text-gray-500">Meetings</div>
                                    <div className="text-2xl font-bold">{targetMetrics.meetings.completed + targetMetrics.meetings.scheduled}</div>
                                </div>
                                <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                    Target: {targetMetrics.meetings.target}
                                </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div className="bg-green h-2 rounded-full" style={{ width: `88%` }}></div>
                            </div>
                            <div className="mt-2 text-sm text-[#16a34a]">
                                {targetMetrics.meetings.scheduled} scheduled
                            </div>
                        </div>

                        <div className="p-4 border rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <div className="text-sm text-gray-500">Conversion Rate</div>
                                    <div className="text-2xl font-bold">{targetMetrics.conversion.rate}</div>
                                </div>
                                <TrendingUp className="w-4 h-4 text-[#22c55e]" />
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div className="bg-green h-2 rounded-full" style={{ width: `62%` }}></div>
                            </div>
                            <div className="mt-2 text-sm text-[#16a34a]">
                                Above target
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Priority Leads */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                       <span className='font-bold'>Priority Leads</span>
                    </div>
                    <div>
                        Ranked by value and conversion probability
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {prioritizedLeads.map((lead, index) => (
                            <div key={index} className="p-4 border rounded-lg">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className="font-medium">{lead.name}</h3>
                                        <p className="text-sm text-gray-500">{lead.type}</p>
                                    </div>
                                    <div className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm ${lead.probability >= 80 ? "bg-[#d1fae5] text-[#065f46]" :
                                            lead.probability >= 70 ? "bg-blue-100 text-blue-800" :
                                                "bg-[#fef9c3] text-[#854d0e]"}
                                    `}>
                                        {lead.probability}% Probability
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 text-sm">
                                    <div>
                                        <span className="text-gray-500">Opportunity</span>
                                        <div className="font-medium">{lead.opportunity}</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Decision Maker</span>
                                        <div className="font-medium">{lead.decisionMaker}</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Urgency</span>
                                        <div className="font-medium capitalize">{lead.urgency}</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Best Times</span>
                                        <div className="font-medium">{lead.bestTimes[0]}</div>
                                    </div>
                                </div>

                                <div className="flex justify-end">
                                    <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">Schedule Meeting</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Suggested Schedule */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        <span className='font-bold'>Suggested Schedule</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            Optimized for high-value conversions
                        </div>
                        <select value={selectedWeek} onChange={(e) => setSelectedWeek(e.target.value)} className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                            <option value="">Select Week</option>
                            <option value="current">Current Week</option>
                            <option value="next">Next Week</option>
                            <option value="following">Following Week</option>
                        </select>
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {suggestedSchedule.map((day, index) => (
                            <div key={index} className="border rounded-lg">
                                <div className="p-3 bg-gray-50 border-b font-medium">
                                    {day.day}
                                </div>
                                <div className="divide-y">
                                    {day.slots.map((slot, slotIndex) => (
                                        <div key={slotIndex} className="p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <div className="font-medium">{slot.lead}</div>
                                                    <div className="text-sm text-gray-500">{slot.type}</div>
                                                </div>
                                                <div className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm flex items-center ${slot.status === 'confirmed' ? 'bg-[#d1fae5] text-[#065f46]' : 'bg-blue-100 text-blue-800'}`}>
                                                    {slot.status === 'confirmed' ?
                                                        <CheckCircle className="w-3 h-3 mr-1" /> :
                                                        <Clock className="w-3 h-3 mr-1" />
                                                    }
                                                    {slot.time}
                                                </div>
                                            </div>
                                            {slot.value && (
                                                <div className="text-sm text-[#16a34a] flex items-center gap-1">
                                                    <DollarSign className="w-3 h-3" />
                                                    Opportunity: {slot.value}
                                                </div>
                                            )}
                                            {slot.status === 'suggested' && (
                                                <div className="flex justify-end mt-2">
                                                    <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">Confirm Slot</button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Main